/** Message deliveries owned by a goal. Saved change records still enforce ownership and revisions. */
export type ManuscriptTask = {
  goalId: string;
  goalRevision?: string | number;
  task?: Record<string, unknown>;
  operation?: unknown;
  /** Historical source evidence; lifecycle and actions belong to manuscriptChange. */
  rewriteSource?: Record<string, unknown>;
  displayTitle?: string;
  explanations?: string[];
  manuscriptChange: Record<string, unknown>;
};
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const text = (value: unknown) => typeof value === 'string' ? value.trim() : '';

export function manuscriptChangeMatches(current: unknown, update: unknown): boolean {
  const a = record(current), b = record(update);
  if (text(b.changeSetId) && b.changeSetId === a.changeSetId) return true;
  const ids = new Set([a.changeId, ...(Array.isArray(a.options) ? a.options.map(option => record(option).changeId) : [])]);
  return Boolean(text(b.changeId) && ids.has(b.changeId));
}

/** Update only the owning task when an approval, decline, or apply changes its lifecycle. */
export function updateManuscriptTasks(metadata: unknown, update: Record<string, unknown>): ManuscriptTask[] | null {
  const tasks = readManuscriptTasks(metadata);
  return tasks?.map(task => manuscriptChangeMatches(task.manuscriptChange, update)
    ? { ...task, manuscriptChange: update.changeSetId
      // A coordinated set's card combines documents. Preserve each task's own payload.
      ? { ...task.manuscriptChange, status: update.status }
      : { ...task.manuscriptChange, ...update, options: update.options ?? task.manuscriptChange.options } } : task) ?? null;
}

/** null means pre-task metadata; an explicit empty/invalid list is authoritative. */
function readToolManuscriptTasks(metadata: unknown): ManuscriptTask[] | null {
  const raw = record(metadata);
  if (Object.prototype.hasOwnProperty.call(raw, 'manuscriptTasks')) {
    if (!Array.isArray(raw.manuscriptTasks)) return [];
    // Never drop a malformed sibling and accidentally turn ambiguity into a match.
    if (raw.manuscriptTasks.some(value => !text(record(value).goalId)
      || !text(record(record(value).manuscriptChange).changeId))) return [];
    return raw.manuscriptTasks as ManuscriptTask[];
  }
  const recovered = manuscriptTasksFromExecutions(raw.toolExecutions);
  if (!recovered.length) return null;
  // Old approval handlers updated only the message-wide lifecycle mirror.
  const mirror = record(raw.manuscriptChange);
  const terminal = ['applied', 'visible', 'declined', 'undone', 'conflict', 'failed'].includes(String(mirror.status));
  return terminal ? recovered.map(task => manuscriptChangeMatches(task.manuscriptChange, mirror)
    ? { ...task, manuscriptChange: { ...task.manuscriptChange, status: mirror.status } } : task) : recovered;
}

/** One preview collection for new messages and legacy mixed chat/edit replies.
 * Existing task lifecycle wins over compatibility source mirrors after apply.
 */
export function readManuscriptTasks(metadata: unknown): ManuscriptTask[] | null {
  const raw = record(metadata);
  const existing = readToolManuscriptTasks(raw);
  if (Object.prototype.hasOwnProperty.call(raw, 'manuscriptTasks')
    && (!Array.isArray(raw.manuscriptTasks) || (raw.manuscriptTasks.length && !existing?.length))) return [];
  const sources = raw.chatRewriteSources;
  if (sources !== undefined && !Array.isArray(sources)) return [];
  const tasks = [...(existing ?? [])];
  const seen = new Set(tasks.flatMap(task => {
    const change = task.manuscriptChange;
    return [change.changeId, ...(Array.isArray(change.options) ? change.options.map(option => record(option).changeId) : [])];
  }));
  for (const rawGroup of sources ?? []) {
    const group = record(rawGroup), source = record(group.source);
    if (!Array.isArray(source.options)) return [];
    const options = source.options.map(record);
    // Unsaved prose is not an actionable preview. Never expose a partial group.
    if (!options.some(option => option.savedChange)) {
      if (options.some(option => !text(option.prose))) return [];
      continue;
    }
    if (!text(group.goalId) || !options.length || options.some(option => {
      const saved = record(option.savedChange);
      return !text(saved.changeId) || !text(saved.documentId) || !text(option.prose)
        || (option.index !== undefined && (!Number.isSafeInteger(option.index) || Number(option.index) < 1))
        || (text(source.documentId) && saved.documentId !== source.documentId);
    })) return [];
    if (options.every(option => seen.has(record(option.savedChange).changeId))) continue;
    // Do not resurrect part of an already represented group from a stale mirror.
    if (options.some(option => seen.has(record(option.savedChange).changeId))) return [];
    const changes = options.map((option, index) => ({
      changeId: text(record(option.savedChange).changeId), documentId: text(record(option.savedChange).documentId),
      optionIndex: option.index === undefined ? index : Number(option.index) - 1, status: 'preview_only',
      ...(text(option.label) ? { label: text(option.label) } : {}), suggestionText: option.prose,
      preview: { schemaVersion: 1, before: source.passage ?? '', after: option.prose,
        replacementText: option.prose, truncated: false, editCount: 1, noOp: false },
    }));
    tasks.push({ goalId: text(group.goalId), rewriteSource: source,
      displayTitle: text(source.label) || text(options[0].label).replace(/\s+Option\s+\d+.*$/i, '') || 'Rewrite alternatives',
      manuscriptChange: { ...changes[0], options: changes },
    });
    changes.forEach(change => seen.add(change.changeId));
  }
  return tasks.length || existing !== null ? tasks : null;
}

/** Adapt legacy tool receipts without rewriting history or inventing option text. */
export function manuscriptTasksFromExecutions(executions: unknown): ManuscriptTask[] {
  if (!Array.isArray(executions)) return [];
  const tasks = new Map<string, ManuscriptTask>();
  for (const value of executions) {
    const execution = record(value);
    if (['approve_manuscript_change', 'apply_manuscript_change_set', 'apply_selected_option', 'apply_wire_insert_follow_up'].includes(String(execution.name))) {
      const result = record(execution.result);
      const data = record(result.data);
      const status = result.status ?? data.status;
      const outcome = result.outcome ?? data.outcome;
      if (!result.error && result.success !== false && result.mutationApplied !== false
        && (result.mutationApplied === true || outcome === 'applied' || status === 'applied'
          || status === 'partially_applied' || (result.success === true && !outcome && !status))) {
        const changeId = text(result.changeId ?? data.changeId);
        const setId = text(result.changeSetId ?? data.changeSetId);
        for (const [id, task] of tasks) {
          const change = task.manuscriptChange;
          const options = Array.isArray(change.options) ? change.options : [];
          if ((!changeId && !setId) || id === changeId || (setId && change.changeSetId === setId)
            || options.some(option => record(option).changeId === changeId)) {
            tasks.set(id, { ...task, manuscriptChange: { ...change, status: 'applied' } });
          }
        }
      }
      continue;
    }
    if (execution.name !== 'propose_manuscript_change') continue;
    const result = record(execution.result);
    if (result.success === false || result.error) continue;
    const change = record(result.data);
    if (!text(change.changeId) || !text(change.status)) continue;
    const args = record(execution.arguments);
    const binding = record(execution.taskResultBinding ?? result.taskResultBinding);
    const revisions = record(binding.relatedGoalRevisions);
    const ids = Object.keys(revisions);
    const goalId = ids.length === 1 ? ids[0] : text(args.goal_key) || `legacy:${text(change.changeId)}`;
    const goalRevision = ids.length === 1 ? revisions[goalId] : args.goal_revision;
    const documentId = text(change.documentId) || text(args.document_id);
    tasks.set(text(change.changeId), { goalId,
      manuscriptChange: { ...change, ...(documentId ? { documentId } : {}) },
      ...(typeof goalRevision === 'string' || typeof goalRevision === 'number' ? { goalRevision } : {}),
      ...(binding.task ? { task: record(binding.task) } : {}),
      ...(args.operation ? { operation: args.operation } : {}),
      ...(Array.isArray(args.replacement_explanations) ? { explanations: args.replacement_explanations.filter((value): value is string => typeof value === "string") } : {}),
      ...(text(args.display_title) ? { displayTitle: text(args.display_title) } : {}),
    });
  }
  return [...tasks.values()];
}
